<script setup>
import { ref, computed } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useVuelidate } from '@vuelidate/core';
import { required, email } from '@vuelidate/validators';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import Button from 'dashboard/components-next/button/Button.vue';

const emit = defineEmits(['close']);

const store = useStore();
const { t } = useI18n();

const agentName = ref('');
const agentEmpId = ref('');
const agentEmail = ref('');
const selectedRoleId = ref('agent');
const createdAgentEmail = ref('');
const createdAgentTempPassword = ref('');

const rules = {
  agentName: { required },
  agentEmpId: { required },
  agentEmail: { required, email },
  selectedRoleId: { required },
};

const v$ = useVuelidate(rules, {
  agentName,
  agentEmpId,
  agentEmail,
  selectedRoleId,
});

const uiFlags = useMapGetter('agents/getUIFlags');
const getCustomRoles = useMapGetter('customRole/getCustomRoles');

const roles = computed(() => {
  const defaultRoles = [
    {
      id: 'administrator',
      name: 'administrator',
      label: t('AGENT_MGMT.AGENT_TYPES.ADMINISTRATOR'),
    },
    {
      id: 'agent',
      name: 'agent',
      label: t('AGENT_MGMT.AGENT_TYPES.AGENT'),
    },
  ];

  const customRoles = getCustomRoles.value.map(role => ({
    id: role.id,
    name: `custom_${role.id}`,
    label: role.name,
  }));

  return [...defaultRoles, ...customRoles];
});

const selectedRole = computed(() =>
  roles.value.find(
    role =>
      role.id === selectedRoleId.value || role.name === selectedRoleId.value
  )
);

const copyTempPassword = async () => {
  try {
    await copyTextToClipboard(createdAgentTempPassword.value);
    useAlert(t('AGENT_MGMT.ADD.TEMP_PASSWORD.COPIED'));
  } catch {
    useAlert(t('AGENT_MGMT.ADD.TEMP_PASSWORD.COPY_ERROR'));
  }
};

const closeModal = () => {
  emit('close');
};

const addAgent = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  try {
    const payload = {
      name: agentName.value,
      email: agentEmail.value,
      emp_id: agentEmpId.value.trim(),
    };

    if (selectedRole.value.name.startsWith('custom_')) {
      payload.custom_role_id = selectedRole.value.id;
    } else {
      payload.role = selectedRole.value.name;
    }

    const response = await store.dispatch('agents/create', payload);
    useAlert(t('AGENT_MGMT.ADD.API.SUCCESS_MESSAGE'));
    if (response.temp_password) {
      createdAgentEmail.value = agentEmail.value;
      createdAgentTempPassword.value = response.temp_password;
      return;
    }
    closeModal();
  } catch (error) {
    const {
      response: {
        data: {
          error: errorResponse = '',
          attributes: attributes = [],
          message: attrError = '',
        } = {},
      } = {},
    } = error;

    let errorMessage = '';
    if (error?.response?.status === 422 && !attributes.includes('base')) {
      errorMessage = t('AGENT_MGMT.ADD.API.EXIST_MESSAGE');
    } else {
      errorMessage = t('AGENT_MGMT.ADD.API.ERROR_MESSAGE');
    }
    useAlert(errorResponse || attrError || errorMessage);
  }
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header
      :header-title="$t('AGENT_MGMT.ADD.TITLE')"
      :header-content="$t('AGENT_MGMT.ADD.DESC')"
    />
    <div
      v-if="createdAgentTempPassword"
      class="flex flex-col w-full gap-4 p-4 border rounded-xl bg-n-alpha-2 border-n-weak"
    >
      <div>
        <h3 class="text-base font-medium text-n-slate-12">
          {{ $t('AGENT_MGMT.ADD.TEMP_PASSWORD.TITLE') }}
        </h3>
        <p class="mt-1 text-sm text-n-slate-11">
          {{
            $t('AGENT_MGMT.ADD.TEMP_PASSWORD.DESC', {
              email: createdAgentEmail,
            })
          }}
        </p>
      </div>

      <div>
        <span class="text-xs font-medium text-n-slate-11">
          {{ $t('AGENT_MGMT.ADD.TEMP_PASSWORD.LABEL') }}
        </span>
        <div
          class="flex items-center justify-between gap-2 p-3 mt-1 rounded-lg bg-n-slate-3"
        >
          <code class="text-sm break-all text-n-slate-12">
            {{ createdAgentTempPassword }}
          </code>
          <Button
            faded
            slate
            type="button"
            :label="$t('AGENT_MGMT.ADD.TEMP_PASSWORD.COPY')"
            @click="copyTempPassword"
          />
        </div>
      </div>

      <p class="text-xs text-n-slate-10">
        {{ $t('AGENT_MGMT.ADD.TEMP_PASSWORD.NOTE') }}
      </p>

      <div class="flex justify-end w-full">
        <Button
          type="button"
          :label="$t('AGENT_MGMT.ADD.TEMP_PASSWORD.DONE')"
          @click="closeModal"
        />
      </div>
    </div>

    <form
      v-else
      class="flex flex-col items-start w-full"
      @submit.prevent="addAgent"
    >
      <div class="w-full">
        <label :class="{ error: v$.agentName.$error }">
          {{ $t('AGENT_MGMT.ADD.FORM.NAME.LABEL') }}
          <input
            v-model="agentName"
            type="text"
            :placeholder="$t('AGENT_MGMT.ADD.FORM.NAME.PLACEHOLDER')"
            @input="v$.agentName.$touch"
            class="block w-full p-2.5 text-sm text-gray-900 bg-gray-50 rounded-lg border border-slate-100 focus:ring-woot-500 focus:border-woot-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-woot-500 dark:focus:border-woot-500"
          />
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.agentEmpId.$error }">
          {{ $t('AGENT_MGMT.ADD.FORM.EMP_ID.LABEL') }}
          <input
            v-model="agentEmpId"
            type="text"
            :placeholder="$t('AGENT_MGMT.ADD.FORM.EMP_ID.PLACEHOLDER')"
            @input="v$.agentEmpId.$touch"
            class="block w-full p-2.5 text-sm text-gray-900 bg-gray-50 rounded-lg border border-slate-100 focus:ring-woot-500 focus:border-woot-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-woot-500 dark:focus:border-woot-500"
          />
          <span v-if="v$.agentEmpId.$error" class="message">
            {{ $t('AGENT_MGMT.ADD.FORM.EMP_ID.ERROR') }}
          </span>
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.selectedRoleId.$error }">
          {{ $t('AGENT_MGMT.ADD.FORM.AGENT_TYPE.LABEL') }}
          <select v-model="selectedRoleId" @change="v$.selectedRoleId.$touch"
            class="block w-full p-2.5 text-sm text-gray-900 bg-gray-50 rounded-lg border border-slate-100 focus:ring-woot-500 focus:border-woot-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-woot-500 dark:focus:border-woot-500"
          >
            <option v-for="role in roles" :key="role.id" :value="role.id">
              {{ role.label }}
            </option>
          </select>
          <span v-if="v$.selectedRoleId.$error" class="message">
            {{ $t('AGENT_MGMT.ADD.FORM.AGENT_TYPE.ERROR') }}
          </span>
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.agentEmail.$error }">
          {{ $t('AGENT_MGMT.ADD.FORM.EMAIL.LABEL') }}
          <input
            v-model="agentEmail"
            type="email"
            :placeholder="$t('AGENT_MGMT.ADD.FORM.EMAIL.PLACEHOLDER')"
            @input="v$.agentEmail.$touch"
            class="block w-full p-2.5 text-sm text-gray-900 bg-gray-50 rounded-lg border border-slate-100 focus:ring-woot-500 focus:border-woot-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-woot-500 dark:focus:border-woot-500"
          />
        </label>
      </div>

      <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
        <Button
          faded
          slate
          type="reset"
          :label="$t('AGENT_MGMT.ADD.CANCEL_BUTTON_TEXT')"
          @click.prevent="closeModal"
        />
        <Button
          type="submit"
          :label="$t('AGENT_MGMT.ADD.FORM.SUBMIT')"
          :disabled="v$.$invalid || uiFlags.isCreating"
          :is-loading="uiFlags.isCreating"
        />
      </div>
    </form>
  </div>
</template>
